import apiClient from '../apiClient';
import type { EmailMessage, GetMessagesOptions, SendMessageOptions, EmailProvider } from './types';

// ✅ ENTERPRISE: Retry system with max 3 attempts + notifications
const MAX_RETRIES = 3;
let gmailRetryCount = 0;
let gmailCircuitOpen = false;
let gmailLastFailure = 0;
let gmailErrorMessage = '';
const CIRCUIT_RESET_MS = 300000; // 5 minutes before allowing retry

// ✅ FIX: In-flight request guard to prevent multiple concurrent requests
let gmailRequestInFlight = false;

// Notification callbacks (set by components)
type NotificationCallback = (message: string, type: 'error' | 'warning' | 'info') => void;
let notificationCallback: NotificationCallback | null = null;

/**
 * Register notification callback for Gmail errors
 */
export function setGmailNotificationCallback(callback: NotificationCallback | null): void {
  notificationCallback = callback;
}

/**
 * Get current Gmail status for UI display
 */
export function getGmailStatus(): {
  isAvailable: boolean;
  retryCount: number;
  errorMessage: string;
  nextRetryIn: number; // seconds
} {
  const nextRetryIn = gmailCircuitOpen
    ? Math.max(0, Math.ceil((CIRCUIT_RESET_MS - (Date.now() - gmailLastFailure)) / 1000))
    : 0;

  return {
    isAvailable: !gmailCircuitOpen,
    retryCount: gmailRetryCount,
    errorMessage: gmailErrorMessage,
    nextRetryIn,
  };
}

/**
 * Reset Gmail circuit breaker (call when user re-authenticates)
 */
export function resetGmailCircuit(): void {
  gmailCircuitOpen = false;
  gmailRetryCount = 0;
  gmailLastFailure = 0;
  gmailErrorMessage = '';
  console.log('🔌 [Gmail] Circuit breaker reset - ready to connect');
}

/**
 * Check if Gmail circuit is open (should skip requests)
 */
export function isGmailCircuitOpen(): boolean {
  if (!gmailCircuitOpen) return false;

  // Auto-reset after timeout
  if (Date.now() - gmailLastFailure > CIRCUIT_RESET_MS) {
    console.log('🔌 [Gmail] Circuit breaker auto-reset after timeout');
    gmailCircuitOpen = false;
    gmailRetryCount = 0;
    return false;
  }

  return true;
}

/**
 * Send notification to UI
 */
function notifyError(message: string, type: 'error' | 'warning' | 'info' = 'error'): void {
  gmailErrorMessage = message;

  if (notificationCallback) {
    notificationCallback(message, type);
  }

  // Also dispatch a custom event for any listeners
  window.dispatchEvent(
    new CustomEvent('gmail:error', {
      detail: { message, type, retryCount: gmailRetryCount, maxRetries: MAX_RETRIES },
    })
  );
}

/**
 * ENTERPRISE V2: Backend façade for Gmail using centralized OAuthManager
 */
export async function initiateAuth(): Promise<void> {
  const { OAuthManager } = await import('../auth/OAuthManager');
  const oauthManager = new OAuthManager();
  await oauthManager.initiateOAuth('gmail');
}

export async function handleCallback(code: string): Promise<boolean> {
  const response = await apiClient.post<{ success: boolean }>('/api/email/gmail/callback', {
    code,
  });

  // Reset circuit on successful auth
  if (response?.success) {
    resetGmailCircuit();
    notifyError('Gmail connected successfully!', 'info');
  }

  return Boolean(response?.success);
}

export async function fetchMessages(options: GetMessagesOptions = {}): Promise<EmailMessage[]> {
  // ✅ CIRCUIT BREAKER: Skip if circuit is open (max retries exceeded)
  if (isGmailCircuitOpen()) {
    const status = getGmailStatus();
    console.log(`🔌 [Gmail] Circuit OPEN - retry in ${status.nextRetryIn}s`);
    throw new Error(
      gmailErrorMessage ||
        '📧 Gmail connection expired. Please reconnect in Settings → Integrations.'
    );
  }

  // ✅ FIX: Prevent multiple concurrent requests - wait for in-flight request
  if (gmailRequestInFlight) {
    console.log('🔌 [Gmail] Request already in flight - skipping duplicate');
    throw new Error('📧 Gmail request in progress');
  }

  gmailRequestInFlight = true;

  try {
    const response = await apiClient.get<{ success: boolean; messages: EmailMessage[] }>(
      '/api/email/gmail/messages',
      { params: options }
    );

    if (!response.success) throw new Error('Failed to fetch Gmail messages');

    // Success - reset retry count
    if (gmailRetryCount > 0) {
      console.log('✅ [Gmail] Connection restored');
      gmailRetryCount = 0;
      gmailErrorMessage = '';
    }

    return response.messages || [];
  } catch (error: any) {
    // Check if this is an auth error
    const isAuthError =
      error?.response?.status === 401 ||
      error?.message?.includes('expired') ||
      error?.message?.includes('reconnect') ||
      error?.response?.data?.error?.includes('expired') ||
      error?.response?.data?.error?.includes('invalid_grant');

    if (isAuthError) {
      gmailRetryCount++;
      console.log(`🔌 [Gmail] Auth failure (attempt ${gmailRetryCount}/${MAX_RETRIES})`);

      if (gmailRetryCount >= MAX_RETRIES) {
        // Max retries reached - open circuit
        gmailCircuitOpen = true;
        gmailLastFailure = Date.now();

        const errorMsg =
          '📧 Gmail connection expired. Please reconnect in Settings → Integrations.';
        gmailErrorMessage = errorMsg;
        notifyError(errorMsg, 'error');

        console.log(`🔌 [Gmail] Circuit OPENED after ${MAX_RETRIES} failures`);
      } else {
        // Notify about retry
        const retryMsg = `📧 Gmail connection issue (attempt ${gmailRetryCount}/${MAX_RETRIES}). Retrying...`;
        notifyError(retryMsg, 'warning');
      }
    }

    throw error;
  } finally {
    // ✅ FIX: Always release the in-flight guard
    gmailRequestInFlight = false;
  }
}

export async function sendMessage(message: SendMessageOptions): Promise<void> {
  await apiClient.post('/api/email/gmail/send', message);
}

export async function deleteMessage(messageId: string): Promise<void> {
  await apiClient.delete(`/api/email/gmail/messages/${messageId}`);
}

export function isSupportedProvider(provider: EmailProvider): boolean {
  return provider === 'gmail';
}
